/**
 * Combined hook for narrator display with theatrical mode integration
 *
 * Combines:
 * - useNarratorLoading (tier-aware loading quips)
 * - useNarratorStateMachine (streaming handoff)
 * - Theatrical mode timing
 *
 * This hook serves as the primary interface for narrator display in results views.
 * It handles the full lifecycle: loading quips -> streaming -> complete.
 *
 * Usage:
 * ```tsx
 * const { tier } = useEntitlements();
 *
 * const {
 *   displayText,
 *   displayState,
 *   voiceStyle,
 *   showTypingAnimation,
 * } = useNarratorDisplay({
 *   tier: tier || 'free',
 *   isAnalyzing: isLoading,
 *   analysisResult: analysis.result,
 *   streamedNarrator: undefined, // Wire when streaming enabled
 *   streamingEnabled: false, // Feature flag
 * });
 * ```
 *
 * Created: 2026-02-03
 */

import { useMemo } from 'react';
import { useNarratorLoading, type UseNarratorLoadingOptions } from './useNarratorLoading';
import { getQuipText, type UserTier } from '../lib/narrator/loadingQuips';

export interface UseNarratorDisplayOptions {
  /** User's subscription tier */
  tier: UserTier;
  /** Whether analysis is in progress */
  isAnalyzing: boolean;
  /** Analysis result (when complete) */
  analysisResult?: {
    narrator_intro?: string;
    summary?: string;
  };
  /** Streamed narrator text (partial) */
  streamedNarrator?: string;
  /** Whether streaming is enabled */
  streamingEnabled?: boolean;
  /** Translation function from useTranslation hook - required for i18n quips */
  t?: (key: string, options?: { defaultValue?: string }) => string;
}

export interface UseNarratorDisplayResult {
  /** Text to display in the narrator area */
  displayText: string;
  /** Current display state */
  displayState: 'idle' | 'loading' | 'streaming' | 'complete';
  /** Voice style for animations */
  voiceStyle: 'playful' | 'balanced' | 'professional';
  /** Whether to show typing animation */
  showTypingAnimation: boolean;
  /** Current loading quip (if in loading state) */
  currentQuip: string | null;
}

/**
 * Combined hook for narrator display with theatrical mode integration
 *
 * Manages the complete narrator display lifecycle with tier-appropriate
 * loading quips, streaming support, and theatrical mode timing.
 *
 * @param options - Configuration options
 * @returns Narrator display state and text
 */
export function useNarratorDisplay({
  tier,
  isAnalyzing,
  analysisResult,
  streamedNarrator,
  streamingEnabled = false,
  t,
}: UseNarratorDisplayOptions): UseNarratorDisplayResult {
  // Get final narrator from result
  const finalNarrator = analysisResult?.narrator_intro;
  const fallbackText = analysisResult?.summary || '';

  // Use the loading quips hook for tier-aware quips
  // Pass t function for i18n translations - without it, falls back to hardcoded English
  const loadingState = useNarratorLoading({
    tier,
    isAnalyzing,
    streamedNarrator: streamingEnabled ? streamedNarrator : undefined,
    finalNarrator,
    fallbackText,
    t,
  });

  // Determine display state
  const displayState = useMemo(() => {
    if (!isAnalyzing && !finalNarrator) return 'idle';
    if (finalNarrator) return 'complete';
    if (streamingEnabled && streamedNarrator && streamedNarrator.length > 10) return 'streaming';
    if (isAnalyzing) return 'loading';
    return 'idle';
  }, [isAnalyzing, finalNarrator, streamingEnabled, streamedNarrator]);

  // Determine display text based on state
  const displayText = useMemo(() => {
    switch (displayState) {
      case 'idle':
        return '';
      case 'loading':
        return loadingState.displayText;
      case 'streaming':
        return streamedNarrator || '';
      case 'complete':
        return finalNarrator || fallbackText;
      default:
        return '';
    }
  }, [displayState, loadingState.displayText, streamedNarrator, finalNarrator, fallbackText]);

  // Show typing animation during loading and streaming
  const showTypingAnimation = displayState === 'loading' || displayState === 'streaming';

  // Get translated quip text when available
  const currentQuipText = useMemo(() => {
    if (displayState !== 'loading' || !loadingState.currentQuip) return null;
    // Use translation if t() provided, otherwise fallback to English
    return t
      ? getQuipText(loadingState.currentQuip, t)
      : loadingState.currentQuip.fallbackText;
  }, [displayState, loadingState.currentQuip, t]);

  return {
    displayText,
    displayState,
    voiceStyle: loadingState.voiceStyle,
    showTypingAnimation,
    currentQuip: currentQuipText,
  };
}
